import { useState, useMemo, useCallback } from 'react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';
import { scanBadge } from '../constants/scanStatus';

const STATUS_BADGE = {
  completed:  { cls: 'am-badge am-badge-completed', text: 'Completed' },
  processing: { cls: 'am-badge am-badge-extracting', text: 'Processing' },
  pending:    { cls: 'am-badge am-badge-queued',     text: 'Pending' },
  failed:     { cls: 'am-badge am-badge-failed',     text: 'Failed' },
};

// content_status='empty' is a third badge state — pipeline ran cleanly,
// produced no transcribable content (silent audio, image-only PDF, etc.).
// Doc is otherwise valid and searchable by filename + metadata.
const EMPTY_BADGE = { cls: 'am-badge am-badge-empty', text: 'No content' };

// Default labels per reason_code. Server-side registry is authoritative
// (backend/domain/extraction/content_reason_codes.py); this is a UI
// fallback for when the server doesn't ship the registry to the client.
// Unknown codes render the generic "No content extracted" label.
const REASON_LABELS = {
  'transcription.empty.low_audio':       'Audio too quiet',
  'transcription.empty.no_speech':       'No speech detected',
  'transcription.empty.silence':         'Silent audio',
  'extraction.empty.image_only_pdf':     'Image-only PDF',
  'extraction.empty.blank_pages':        'Blank pages',
  'archive.empty.no_useful_files':       'Empty archive',
  'archive.empty.all_duplicates':        'Already in case',
  'keyframes.empty.no_visual_change':    'Static video',
  'keyframes.empty.black_frames':        'Black-frame video',
};

function emptyLabel(reasonCode) {
  return REASON_LABELS[reasonCode] || EMPTY_BADGE.text;
}

const MAX_VISIBLE = 20;

function fmtSize(b) {
  if (!b) return '—';
  if (b < 1024) return `${Math.round(b)} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
}

function dateLabel(iso) {
  try {
    const dt = new Date(iso);
    const now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = dt.toDateString() === yesterday.toDateString();
    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return 'Unknown';
  }
}

function timeStr(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Group documents by calendar date, newest first */
function groupByDate(documents) {
  const sorted = [...documents]
    .filter(d => d.created_at)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (sorted.length === 0) return [];

  const groups = [];
  let currentLabel = null;
  let currentDocs = [];

  for (const doc of sorted) {
    const label = dateLabel(doc.created_at);
    if (label !== currentLabel) {
      if (currentDocs.length) groups.push({ label: currentLabel, docs: currentDocs });
      currentLabel = label;
      currentDocs = [doc];
    } else {
      currentDocs.push(doc);
    }
  }
  if (currentDocs.length) groups.push({ label: currentLabel, docs: currentDocs });
  return groups;
}

// A doc's effective status for UI grouping. Adds an 'empty' bucket on top
// of the existing pipeline statuses (completed/failed/processing/pending).
// `doc.outcome_status === 'empty'` is the Tier C signal that the pipeline
// ran cleanly but produced no extractable content — different from a real
// failure, badged separately, retried separately.
function effectiveStatus(doc) {
  if (doc.status === 'completed' && doc.outcome_status === 'empty') return 'empty';
  return doc.status;
}

function GroupSummary({ docs }) {
  const completed = docs.filter(d => effectiveStatus(d) === 'completed').length;
  const empty = docs.filter(d => effectiveStatus(d) === 'empty').length;
  const failed = docs.filter(d => effectiveStatus(d) === 'failed').length;
  // Failed takes priority — it's the user-actionable signal.
  if (failed > 0) return <span className="am-badge am-badge-failed">{failed} failed</span>;
  // Then empty — informational, lower priority than failed but still surfaced.
  if (empty > 0 && empty + completed === docs.length) {
    return <span className="am-badge am-badge-empty">{empty} no content</span>;
  }
  if (completed + empty === docs.length) return <span className="am-badge am-badge-completed">All done</span>;
  return <span className="am-badge am-badge-queued">{docs.length - completed - empty} in progress</span>;
}

function DocRow({ doc, showCost }) {
  const scan = scanBadge(doc);
  const eff = effectiveStatus(doc);
  let badge;
  let tooltip;
  if (scan) {
    // Malware scan state supersedes the pipeline status badge.
    badge = { cls: `am-badge ${scan.amCls}`, text: scan.longLabel };
    tooltip = scan.title;
  } else if (eff === 'empty') {
    badge = { cls: EMPTY_BADGE.cls, text: emptyLabel(doc.outcome_reason_code) };
    tooltip = doc.outcome_reason_code || 'No content extracted';
  } else {
    badge = STATUS_BADGE[eff] || STATUS_BADGE.pending;
  }
  const costCents = doc.processing_cost_cents;
  return (
    <div className="am-row">
      <div className="am-row-header">
        <span className="am-filename">{doc.filename}</span>
        <span className="am-meta">
          {fmtSize(doc.file_size)} · {timeStr(doc.created_at)}
          {showCost && costCents > 0 && ` · $${(costCents / 100).toFixed(2)}`}
        </span>
        <span className={badge.cls} title={tooltip}>{badge.text}</span>
      </div>
    </div>
  );
}

export default function UploadHistory({ documents, failedCount, caseId, onRetryComplete }) {
  const { pricing } = useApp();
  const showCost = pricing?.show_costs !== false;
  const [panelOpen, setPanelOpen] = useState(true);
  const [openGroups, setOpenGroups] = useState(new Set([0]));
  const [showAll, setShowAll] = useState(false);
  const [retryState, setRetryState] = useState('idle'); // idle | loading | done | error

  const groups = useMemo(() => groupByDate(documents || []), [documents]);
  const totalFiles = (documents || []).filter(d => d.created_at).length;

  const handleRetry = useCallback(async (e) => {
    e.stopPropagation();
    setRetryState('loading');
    try {
      // Retry only docs whose pipeline FAILED. Docs that are
      // status='completed' outcome_status='empty' are intentionally
      // excluded — they processed cleanly and reprocessing them with
      // the same input would produce the same empty result. They get
      // their own per-row retry affordance with appropriate remediation
      // (e.g. "apply gain boost") in a follow-up.
      const failedDocs = (documents || []).filter(
        d => d.status === 'failed' && d.outcome_status !== 'empty'
      );
      let ok = 0;
      for (const doc of failedDocs) {
        try {
          const resp = await api.post(`/processing/documents/${doc.id}/reprocess`, {});
          if (resp) ok++;
        } catch { /* continue */ }
      }
      setRetryState(ok > 0 ? 'done' : 'error');
      if (ok > 0) onRetryComplete?.();
      setTimeout(() => setRetryState('idle'), 4000);
    } catch {
      setRetryState('error');
      setTimeout(() => setRetryState('idle'), 4000);
    }
  }, [caseId, documents, onRetryComplete]);

  if (!totalFiles) return null;

  const subtitle = `${totalFiles} file${totalFiles !== 1 ? 's' : ''}`;

  function toggleGroup(idx) {
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  // Cap visible docs per group when not expanded
  let visibleCount = 0;

  return (
    <div className="am-panel">
      <div className="am-panel-header" onClick={() => setPanelOpen(o => !o)}>
        <span className={`am-caret ${panelOpen ? 'open' : ''}`}>▶</span>
        <span className="am-title">📋 Upload History</span>
        <span className="am-subtitle">{subtitle}</span>
        {failedCount > 0 && retryState === 'idle' && (
          <button className="btn btn-sm btn-warning am-retry-btn" onClick={handleRetry}>
            🔄 Retry {failedCount} Failed
          </button>
        )}
        {retryState === 'loading' && <span className="am-meta">Retrying…</span>}
        {retryState === 'done' && <span className="am-badge am-badge-completed">✅ Queued</span>}
        {retryState === 'error' && <span className="am-badge am-badge-failed">❌ Failed</span>}
      </div>

      {panelOpen && (
        <div className="am-panel-body">
          {groups.map((group, gi) => {
            const cappedDocs = [];
            for (const doc of group.docs) {
              if (!showAll && visibleCount >= MAX_VISIBLE) break;
              cappedDocs.push(doc);
              visibleCount++;
            }
            if (cappedDocs.length === 0) return null;

            // Single-file groups render as flat rows with date label
            if (cappedDocs.length === 1) {
              const doc = cappedDocs[0];
              const badge = STATUS_BADGE[doc.status] || STATUS_BADGE.pending;
              const costCents = doc.processing_cost_cents;
              return (
                <div key={gi} className="am-row">
                  <div className="am-row-header">
                    <span className="am-filename">{doc.filename}</span>
                    <span className="am-meta">
                      {fmtSize(doc.file_size)} · {timeStr(doc.created_at)}
                      {showCost && costCents > 0 && ` · $${(costCents / 100).toFixed(2)}`}
                    </span>
                    <span className={badge.cls}>{badge.text}</span>
                  </div>
                </div>
              );
            }

            // Multi-file groups get a collapsible date header
            return (
              <div key={gi} className="am-group">
                <div className="am-group-header" onClick={() => toggleGroup(gi)}>
                  <span className={`am-caret ${openGroups.has(gi) ? 'open' : ''}`}>▶</span>
                  <span className="am-icon">📁</span>
                  <span className="am-label">{group.label} — {group.docs.length} files</span>
                  <GroupSummary docs={group.docs} />
                  <span className="am-count">{group.docs.length}</span>
                </div>
                {openGroups.has(gi) && (
                  <div className="am-group-body">
                    {cappedDocs.map(doc => <DocRow key={doc.id} doc={doc} showCost={showCost} />)}
                    {cappedDocs.length < group.docs.length && (
                      <div className="am-show-more" onClick={() => setShowAll(true)}>
                        Show all {group.docs.length} files…
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {!showAll && totalFiles > MAX_VISIBLE && (
            <div className="am-show-more" onClick={() => setShowAll(true)}>
              Show all {totalFiles} files…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
